import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import ScatterText from '../common/ScatterText';
import Icon from '../common/Icon';
import './About.css';
import './about-responsive.css';
import profileImage from '../../assets/profile2.jpg';
const resume = require('../../assets/resume.pdf');

interface AboutProps {
  className?: string;
}

const About: React.FC<AboutProps> = ({ className = '' }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.2,
        duration: 0.5
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1, 
      transition: { 
        type: "spring",
        stiffness: 100,
        damping: 12,
        duration: 0.5 
      }
    }
  };
  
  const imageVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      transition: { 
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.7
      }
    }
  };

  return (
    <section id="about" className={`about-section ${className}`}>
      <Container>
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="about-content"
        >
          {/* Content Row - Image and Text */}
          <Row className="align-items-center content-row">
            {/* Image column with floating badges */}
            <Col lg={5} md={12} className="mb-5 mb-lg-0">
              <motion.div 
                className="about-image-container"
                variants={imageVariants}
              >
                <motion.div 
                  className="about-image"
                  whileHover={{ 
                    scale: 1.02,
                    transition: { duration: 0.3 } 
                  }}
                  animate={{
                    boxShadow: ['0 10px 20px rgba(0,0,0,0.15)', '0 15px 30px rgba(0,0,0,0.25)', '0 10px 20px rgba(0,0,0,0.15)']
                  }}
                  transition={{ 
                    boxShadow: { 
                      repeat: Infinity, 
                      duration: 3, 
                      ease: "easeInOut" 
                    }
                  }}
                >
                  <motion.div 
                    className="about-image-inner"
                    animate={{
                      borderColor: ['rgba(255,255,255,0.1)', 'rgba(255,255,255,0.3)', 'rgba(255,255,255,0.1)']
                    }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <img
                      src={profileImage}
                      alt="Muhammad Rahim"
                      className="img-fluid"
                    />
                  </motion.div>
                </motion.div>

                {/* Floating Glass Badges */}
                <motion.div 
                  className="about-badge badge-top-left"
                  initial={{ scale: 0, rotate: -10 }}
                  animate={{ 
                    scale: 1, 
                    rotate: 0,
                    y: [0, -5, 0]
                  }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 120, 
                    delay: 0.3,
                    y: { repeat: Infinity, duration: 3, ease: "easeInOut" }
                  }}
                  whileHover={{ scale: 1.08 }}
                >
                  <Icon icon="FaGraduationCap" /> <span>FAST-NUCES</span>
                </motion.div>

                <motion.div 
                  className="about-badge badge-bottom-right"
                  initial={{ scale: 0, rotate: 10 }}
                  animate={{ 
                    scale: 1, 
                    rotate: 0,
                    y: [0, 5, 0]
                  }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 120, 
                    delay: 0.5,
                    y: { repeat: Infinity, duration: 3.5, ease: "easeInOut" }
                  }}
                  whileHover={{ scale: 1.08 }}
                >
                  <Icon icon="FaCode" /> <span>BS CS</span>
                </motion.div>
              </motion.div>
            </Col>

            {/* Description column */}
            <Col lg={7} md={12}>
              <motion.div
                variants={itemVariants}
                className="section-heading mb-4"
              >
                <span className="about-category-chip px-3 py-1.5 rounded-pill mb-3 d-inline-block">
                  About Me
                </span>
                <div className="section-title-container">
                  <div className="section-title-line">
                    <ScatterText text="Muhammad Rahim" className="section-title-scatter name-text" />
                  </div>
                  <div className="section-title-line">
                    <ScatterText text="CS Student & " className="section-title-scatter" />
                    <ScatterText text="Developer" className="section-title-scatter" />
                  </div>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="about-text-container">
                <div className="about-quote-box mb-4 ps-3 py-2 border-start border-3">
                  <p className="mb-0">
                    "Blending logical precision with high-end aesthetic design to craft state-of-the-art software systems."
                  </p>
                </div>
                <p className="about-text">
                  Hi, I'm <strong>Muhammad Rahim</strong>, a Computer Science student at FAST Lahore who enjoys building creative and impactful digital solutions. From developing C++ applications like LexiCraft and CatchAlphabets to working on full-stack web projects and interactive React interfaces with smooth animations, I love exploring different domains of technology.
                </p>
                <p className="about-text">
                  I'm skilled in Python, C++, SQL, React, Django, HTML, CSS, JavaScript, Bootstrap, and Framer Motion, with additional experience in data science tools like Pandas, NumPy, Matplotlib, Seaborn, and Scikit-learn. Currently, I've been diving deeper into AI/ML concepts, working on areas like feature engineering to strengthen my foundation.
                </p>
                <p className="about-text">
                  I've also achieved milestones such as gold and silver medals in the World Scholar's Cup and a Django certification from the University of Michigan. Beyond coding, I enjoy teaching, volleyball, cricket, and exploring design ideas. For me, technology isn't just about solving problems — it's about blending logic with creativity to craft meaningful experiences.
                </p>
              </motion.div>
            </Col>
          </Row>
          
          {/* Info Cards Grid */}
          <Row className="info-row mt-5">
            <Col xs={12}>
              <motion.div variants={itemVariants} className="about-info full-width-info">
                <Row className="g-4">
                  <Col md={3} sm={6} className="mb-3 mb-md-0">
                    <div className="info-item">
                      <div className="info-item-icon">
                        <Icon icon="FaUser" />
                      </div>
                      <h6>Name</h6>
                      <p>Muhammad Rahim</p>
                    </div>
                  </Col>
                  <Col md={3} sm={6} className="mb-3 mb-md-0">
                    <div className="info-item">
                      <div className="info-item-icon">
                        <Icon icon="FaEnvelope" />
                      </div>
                      <h6>Email</h6>
                      <p>muhammadrahimusman@gmail.com</p>
                    </div>
                  </Col>
                  <Col md={3} sm={6} className="mb-3 mb-md-0">
                    <div className="info-item">
                      <div className="info-item-icon">
                        <Icon icon="FaMapMarkerAlt" />
                      </div>
                      <h6>Location</h6>
                      <p>Lahore, Pakistan</p>
                    </div>
                  </Col>
                  <Col md={3} sm={6} className="mb-3 mb-md-0">
                    <div className="info-item">
                      <div className="info-item-icon">
                        <Icon icon="FaCalendarCheck" />
                      </div>
                      <h6>Availability</h6>
                      <p>Open for Opportunities</p>
                    </div>
                  </Col>
                </Row>
              </motion.div>
            </Col>
          </Row>
          
          {/* Buttons Row - Centered */}
          <Row className="buttons-row">
            <Col xs={12} className="text-center">
              <motion.div 
                variants={itemVariants}
                className="about-buttons-wrapper"
              >
                <Link to="/contact">
                  <motion.div 
                    className="about-button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    Contact Me
                  </motion.div>
                </Link>
                <motion.a 
                  href={resume}
                  className="about-button-outline"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  Download CV
                </motion.a>
              </motion.div>
            </Col>
          </Row>
        </motion.div>
      </Container>
    </section>
  );
};

export default About;